import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { zahlungenApi } from '../api';

const TYPEN = ['Sparvertrag', 'Kredit', 'Versicherung', 'Abo'];
const UNTERTYPEN = {
  'Sparvertrag': ['Aktiensparplan', 'ETF-Sparplan', 'Krypto-Sparplan', 'Tagesgeld', 'Festgeld', 'Bausparvertrag', 'Lebensversicherung', 'Sonstiges'],
  'Kredit': ['Ratenkredit', 'Hypothek', 'Autokredit', 'Dispositionskredit', 'Sonstiges'],
  'Versicherung': ['Haftpflicht', 'KFZ', 'Hausrat', 'Kranken', 'Lebens', 'Berufsunfähigkeit', 'Rechtsschutz', 'Sonstiges'],
  'Abo': ['Streaming', 'Software', 'Zeitung', 'Fitness', 'Sonstiges'],
};

export default function ZahlungFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [form, setForm] = useState({
    name: '', typ: 'Sparvertrag', untertyp: '', anbieter: '',
    betrag: '', intervall: 'monatlich', richtung: 'einzahlung',
    beginn: '', ende: '', zinssatz: '', aktueller_wert: '',
    status: 'aktiv', notizen: '', vertragsnummer: '', verwendungszweck: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (isEdit) laden(); }, [id]);

  // Richtung automatisch setzen je nach Typ
  useEffect(() => {
    if (!isEdit) {
      setForm(f => ({
        ...f,
        richtung: f.typ === 'Sparvertrag' ? 'einzahlung' : 'ausgabe',
        untertyp: UNTERTYPEN[f.typ]?.[0] || ''
      }));
    }
  }, [form.typ]);

  async function laden() {
    try {
      const res = await zahlungenApi.get(id);
      const d = res.data;
      setForm({
        name: d.name||'', typ: d.typ||'Sparvertrag', untertyp: d.untertyp||'',
        anbieter: d.anbieter||'', betrag: d.betrag||'', intervall: d.intervall||'monatlich',
        richtung: d.richtung||'ausgabe', beginn: d.beginn||'', ende: d.ende||'',
        zinssatz: d.zinssatz||'', aktueller_wert: d.aktueller_wert||'',
        status: d.status||'aktiv', notizen: d.notizen||'', vertragsnummer: d.vertragsnummer||'', verwendungszweck: d.verwendungszweck||''
      });
    } catch(e) { setError('Fehler beim Laden'); }
  }

  async function speichern(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const data = { ...form,
        betrag: parseFloat(form.betrag),
        zinssatz: form.zinssatz ? parseFloat(form.zinssatz) : null,
        aktueller_wert: form.aktueller_wert ? parseFloat(form.aktueller_wert) : null,
        vertragsnummer: form.vertragsnummer || null,
        verwendungszweck: form.verwendungszweck || null,
      };
      if (isEdit) await zahlungenApi.update(id, data);
      else await zahlungenApi.create(data);
      navigate('/zahlungen');
    } catch(e) { setError(e.response?.data?.error || 'Fehler beim Speichern'); }
    setLoading(false);
  }

  const inp = { width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #334155', color: '#f1f5f9', background: '#0f172a', fontSize: '14px', boxSizing: 'border-box' };
  const lbl = { display: 'block', marginBottom: '6px', fontWeight: '600', color: '#94a3b8', fontSize: '12px' };
  const isSpar = form.typ === 'Sparvertrag';

  return (
    <div style={{ padding: '24px', maxWidth: '600px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <button onClick={() => navigate('/zahlungen')} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#818cf8' }}>←</button>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#f1f5f9' }}>{isEdit ? '✏️ Zahlung bearbeiten' : '➕ Neue Zahlung'}</h2>
      </div>

      {error && <div style={{ background: '#431407', color: '#fca5a5', border: '1px solid #ef4444', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>{error}</div>}

      <form onSubmit={speichern} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* Typ Auswahl als Buttons */}
        <div>
          <label style={lbl}>Typ *</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
            {TYPEN.map(t => (
              <button key={t} type="button" onClick={() => setForm({...form, typ: t})}
                style={{ padding: '10px 4px', border: `2px solid ${form.typ === t ? '#818cf8' : '#334155'}`,
                  borderRadius: '8px', background: form.typ === t ? '#1e1b4b' : '#1e293b',
                  cursor: 'pointer', fontWeight: form.typ === t ? '700' : '400',
                  color: form.typ === t ? '#818cf8' : '#94a3b8', fontSize: '12px' }}>
                {t === 'Sparvertrag' ? '💰' : t === 'Kredit' ? '🏦' : t === 'Versicherung' ? '🛡️' : '📱'}<br/>{t}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label style={lbl}>Name *</label>
          <input style={inp} value={form.name} onChange={e => setForm({...form, name: e.target.value})}
            placeholder={isSpar ? 'z.B. ETF-Sparplan DWS' : 'z.B. KFZ-Versicherung'} required />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
          <div>
            <label style={lbl}>Untertyp</label>
            <select style={inp} value={form.untertyp} onChange={e => setForm({...form, untertyp: e.target.value})}>
              <option value="">-- wählen --</option>
              {(UNTERTYPEN[form.typ] || []).map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Anbieter</label>
            <input style={inp} value={form.anbieter} onChange={e => setForm({...form, anbieter: e.target.value})}
              placeholder="z.B. ING, Allianz..." />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
          <div>
            <label style={lbl}>Betrag (€) *</label>
            <input style={inp} type="number" step="0.01" min="0" value={form.betrag}
              onChange={e => setForm({...form, betrag: e.target.value})} placeholder="0.00" required />
          </div>
          <div>
            <label style={lbl}>Intervall *</label>
            <select style={inp} value={form.intervall} onChange={e => setForm({...form, intervall: e.target.value})}>
              <option value="monatlich">Monatlich</option>
              <option value="vierteljaehrlich">Vierteljährlich</option>
              <option value="halbjaehrlich">Halbjährlich</option>
              <option value="jaehrlich">Jährlich</option>
              <option value="einmalig">Einmalig</option>
            </select>
          </div>
        </div>

        {/* Richtung */}
        <div>
          <label style={lbl}>Richtung *</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {[['einzahlung','📥 Einzahlung (Sparvertrag)','#10b981'],['ausgabe','📤 Ausgabe (Kosten)','#ef4444']].map(([val, label, farbe]) => (
              <button key={val} type="button" onClick={() => setForm({...form, richtung: val})}
                style={{ padding: '12px', border: `2px solid ${form.richtung === val ? farbe : '#334155'}`,
                  borderRadius: '8px', background: form.richtung === val ? farbe + '22' : '#1e293b',
                  cursor: 'pointer', fontWeight: form.richtung === val ? '700' : '400',
                  color: form.richtung === val ? farbe : '#94a3b8', fontSize: '13px' }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Zinssatz + aktueller Wert nur bei Spar/Kredit */}
        {(form.typ === 'Sparvertrag' || form.typ === 'Kredit') && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
            <div>
              <label style={lbl}>Zinssatz (% p.a.)</label>
              <input style={inp} type="number" step="0.01" value={form.zinssatz}
                onChange={e => setForm({...form, zinssatz: e.target.value})} placeholder="z.B. 3.5" />
            </div>
            {isSpar && (
              <div>
                <label style={lbl}>Aktueller Wert (€)</label>
                <input style={inp} type="number" step="0.01" value={form.aktueller_wert}
                  onChange={e => setForm({...form, aktueller_wert: e.target.value})} placeholder="z.B. 12500" />
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
          <div>
            <label style={lbl}>Beginn</label>
            <input style={inp} type="date" value={form.beginn} onChange={e => setForm({...form, beginn: e.target.value})} />
          </div>
          <div>
            <label style={lbl}>Ende / Laufzeit bis</label>
            <input style={inp} type="date" value={form.ende} onChange={e => setForm({...form, ende: e.target.value})} />
          </div>
        </div>

        <div>
          <label style={lbl}>Notizen</label>
          <textarea style={{ ...inp, minHeight: '80px', resize: 'vertical' }} value={form.notizen}
            onChange={e => setForm({...form, notizen: e.target.value})}
            />
            </div>
            <div>
              <label style={lbl}>Vertragsnummer</label>
              <input style={inp} value={form.vertragsnummer}
                placeholder="z.B. VS-123456"
                onChange={e => setForm({...form, vertragsnummer: e.target.value})}
            />
            </div>
            <div>
              <label style={lbl}>Verwendungszweck</label>
              <input style={inp} value={form.verwendungszweck}
                placeholder="z.B. aus Kontoauszug"
                onChange={e => setForm({...form, verwendungszweck: e.target.value})} placeholder="Optionale Notizen..." />
        </div>

        <div style={{ display: 'flex', gap: '12px', paddingTop: '8px' }}>
          <button type="button" onClick={() => navigate('/zahlungen')}
            style={{ flex: 1, padding: '12px', background: '#334155', color: '#94a3b8', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', minHeight: 44 }}>Abbrechen</button>
          <button type="submit" disabled={loading}
            style={{ flex: 2, padding: '12px', background: '#818cf8', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', opacity: loading ? 0.5 : 1, minHeight: 44 }}>
            {loading ? 'Speichern...' : (isEdit ? '💾 Speichern' : '➕ Zahlung hinzufügen')}
          </button>
        </div>
      </form>
    </div>
  );
}
